import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { formatFileSize } from '@/utils/photoCompression';

export interface UploadProgressData {
  id: string;
  fileName: string;
  progress: number; // 0-100
  status: 'uploading' | 'compressing' | 'success' | 'error';
  originalSize?: number;
  compressedSize?: number;
  error?: string;
}

interface Props {
  uploads: UploadProgressData[];
  onRetry?: (id: string) => void;
  onCancel?: (id: string) => void;
}

export const UploadProgress = ({ uploads, onRetry, onCancel }: Props) => {
  const { t } = useTranslation();

  if (uploads.length === 0) return null;

  const getStatusText = (upload: UploadProgressData) => {
    // Check if this is a rotation upload by the filename
    const isRotation = upload.fileName.startsWith('rotated-');

    switch (upload.status) {
      case 'compressing':
        return isRotation ? t('rotating_image') : t('compressing_image');
      case 'uploading':
        return isRotation ? t('rotating_image') : t('uploading_image');
      case 'success':
        return isRotation ? t('rotation_complete') : t('upload_complete');
      case 'error':
        return (
          upload.error ||
          (isRotation ? t('rotation_failed') : t('upload_failed'))
        );
      default:
        return '';
    }
  };

  const getStatusColor = (status: UploadProgressData['status']) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      case 'uploading':
      case 'compressing':
        return 'primary';
      default:
        return 'primary';
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 16,
        left: 16,
        right: 16,
        zIndex: 1000,
        maxHeight: '50vh',
        overflow: 'auto',
      }}
    >
      {uploads.map(upload => (
        <Box
          key={upload.id}
          sx={{
            background: 'white',
            borderRadius: 2,
            p: 2,
            mb: 1,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            border: '1px solid #e0e0e0',
          }}
        >
          {/* File info header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant='body2' sx={{ fontWeight: 'medium' }}>
              {getStatusText(upload)}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {upload.status === 'error' && onRetry && (
                <Typography
                  variant='caption'
                  sx={{
                    color: 'primary.main',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                  onClick={() => onRetry(upload.id)}
                >
                  {t('retry')}
                </Typography>
              )}
              {(upload.status === 'uploading' ||
                upload.status === 'compressing') &&
                onCancel && (
                  <Typography
                    variant='caption'
                    sx={{
                      color: 'text.secondary',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                    onClick={() => onCancel(upload.id)}
                  >
                    {t('cancel')}
                  </Typography>
                )}
            </Box>
          </Box>

          {/* Progress bar */}
          <Box
            sx={{
              height: 6,
              borderRadius: 3,
              mb: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.12)',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <Box
              sx={{
                height: '100%',
                backgroundColor:
                  getStatusColor(upload.status) === 'success'
                    ? '#4caf50'
                    : getStatusColor(upload.status) === 'error'
                      ? '#f44336'
                      : '#1976d2',
                width: `${upload.progress}%`,
                transition: 'width 0.3s ease',
                borderRadius: 'inherit',
              }}
            />
          </Box>

          {/* Status and details */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant='caption' color='text.secondary'>
                {upload.progress}%
              </Typography>

              {upload.originalSize &&
                upload.compressedSize &&
                upload.status === 'success' && (
                  <Typography variant='caption' color='success.main'>
                    {formatFileSize(upload.originalSize)} →{' '}
                    {formatFileSize(upload.compressedSize)} (
                    {Math.round(
                      (1 - upload.compressedSize / upload.originalSize) * 100
                    )}
                    % {t('saved')})
                  </Typography>
                )}
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

// Hook for managing upload progress
export const useUploadProgress = () => {
  const [uploads, setUploads] = useState<UploadProgressData[]>([]);

  const addUpload = (upload: Omit<UploadProgressData, 'progress'>) => {
    const newUpload: UploadProgressData = { ...upload, progress: 0 };
    setUploads(prev => [...prev, newUpload]);
    return newUpload.id;
  };

  const updateUpload = (id: string, updates: Partial<UploadProgressData>) => {
    setUploads(prev =>
      prev.map(upload =>
        upload.id === id ? { ...upload, ...updates } : upload
      )
    );
  };

  const removeUpload = (id: string) => {
    setUploads(prev => prev.filter(upload => upload.id !== id));
  };

  const clearCompleted = () => {
    setUploads(prev =>
      prev.filter(
        upload => upload.status !== 'success' && upload.status !== 'error'
      )
    );
  };

  // Auto-remove successful uploads after 3 seconds
  useEffect(() => {
    const successfulUploads = uploads.filter(u => u.status === 'success');

    if (successfulUploads.length > 0) {
      const timer = setTimeout(() => {
        successfulUploads.forEach(upload => removeUpload(upload.id));
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [uploads]);

  return {
    uploads,
    addUpload,
    updateUpload,
    removeUpload,
    clearCompleted,
  };
};
